<template>
  <div class="o-ring-assistant__container">
    <div class="o-ring-assistant__container__headline">
      <h1 class="h1--underline">
        {{ i18n.tv('Frontend.ORing.Assistant', 'ORingConfiguratorImport') }}
      </h1>
      <NavigationClose @click="showPopup=true" />
    </div>
    <div class="o-ring-assistant__content">
      <ORingAssistantSteps />
    </div>
    <LeaveConfirmationPopup
      :show="showPopup"
      :size="POPUP.Size.small"
      :title="i18n.tv('discardPromptTitle', 'ORingConfigurator')"
      :content="i18n.tv('Frontend.Wizard.LeaveDescription', 'ORingConfiguratorImport')"
      :stay-button-text="i18n.tv('Frontend.Wizard.Stay', 'ORingConfiguratorImport')"
      :leave-button-text="i18n.tv('Frontend.Wizard.Leave', 'ORingConfiguratorImport')"
      @update:show="showPopup = $event"
      @leave="closeORingAssistant"
    />
  </div>
</template>
<script setup lang="ts">
import useBreadcrumb from '~/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import {POPUP} from '~/assets/js/constants'
import ORingAssistantSteps from '~/components/oring-calculator/ORingAssistantSteps.vue'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import {getORingSeoOptions} from '~/composables/oring-helper'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'
import {useOringNavigationGuard} from '~/composables/oring/useOringNavigationGuard'

const oringStore = useORingCalculatorStore()
const {scrollToTop} = useOringScrollToTop()

// Add SEO metadata
const seoData = createSeoMetaInfo(getORingSeoOptions('oring-assistant', 'Frontend.ORing.Assistant', 'ORingConfiguratorImport'))
useSeoMeta(seoData)

const i18n = useI18nExtended()
const route = useRoute()
const router = useRouter()
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value

const head = {
  ...i18nHead,
  title: i18n.tv('pageTitle', 'ORingConfigurator')
}
useHead(head)

const showPopup = ref(false)
useBreadcrumb([
  {
    label: i18n.tv('oRingCalculator', 'breadcrumbs'),
    path: '/oring-calculator'
  },
  {
    label: i18n.tv('ORingAssistant', 'breadcrumbs'),
    path: '/oring-calculator/oring-assistant'
  }
])

const app = useNuxtApp()

const {confirmLeave, clearPendingRoute} = useOringNavigationGuard(
  router,
  showPopup,
  '/oring-calculator/oring-assistant',
  ['/oring-calculator/design-cockpit', '/oring-calculator/material-assistant', '/oring-calculator/oring-assistant/details']
)

const closeORingAssistant = () => {
  showPopup.value = false
  oringStore.resetAssistantStates()

  const pendingRoute = confirmLeave()
  if (pendingRoute) {
    navigateTo(pendingRoute, {replace: true})
    clearPendingRoute()
  } else {
    navigateTo(app.$globalization.localePath('/oring-calculator'), {replace: true})
  }
}

onMounted(() => {
  scrollToTop('auto')
})

</script>
<style lang="scss">
.o-ring-assistant__container {
  background-color: $color__white;
  margin-bottom: 1.5rem;

  @include breakpoint-down($md) {
    padding-bottom: 0;
    margin-bottom: 2rem;
  }

  .o-ring-assistant__content {
    margin-top: 1.25rem;
    padding: 0 2rem;

    @include breakpoint-down($md) {
      padding: 0 1rem;
    }
  }

  &__headline {
    display: flex;
    padding: 0 2rem 2rem;
    justify-content: space-between;
    color: $color__white;
    padding-top: .5rem;

    @include breakpoint-down($md) {
      padding: 0 1rem 1rem;
    }

    .icon.icon--svg {
      font-size: 2rem;
      fill: $color__fst__primary;
      cursor: pointer;
    }

    h1 {
      margin:0;
    }
  }

  .popup__wrapper {
    .popup__content {
      padding-bottom: 1rem;
    }
  }

  .popup-content {
    padding: 1rem 0 0;
    font-weight: $base-text-font-weight-light;

    .popup-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1rem;
    }
  }

  h5 {
    margin: -.5rem 0 0;
    font-size: 1.125rem;
  }
}
</style>
